import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { addDoc, collection, deleteDoc, doc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuBriefcase, LuMail, LuPhone, LuUsers } from 'react-icons/lu';
import { getInitials } from '../../components/ui/Avatar';
import { Field, TextArea, TextInput } from '../../components/form/FormControls';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import RecordListModal from './components/RecordListModal';
import { customerName } from './customerColumns';

const EMPTY_PERSON = {
  firstName: '',
  lastName: '',
  position: '',
  department: '',
  email: '',
  phone: '',
  notes: '',
};

// Same rule as the browser's own e-mail field check in the old form
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+$/;

// Newest first (records without a date keep their order)
const byCreatedDesc = (a, b) => {
  if (a.createdAt && b.createdAt) {
    const aTime = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
    const bTime = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
    return bTime - aTime;
  }
  return 0;
};

/** A customer's contacts (CustomerAuthorizedPersonnel): list, add, edit, delete. */
const AuthorizedPersonnelModal = ({ customer, onClose }) => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState(EMPTY_PERSON);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchPersonnel = useCallback(async () => {
    try {
      const snapshot = await getDocs(query(collection(db, 'CustomerAuthorizedPersonnel'), where('customerId', '==', customer.id)));
      setPersonnel(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })).sort(byCreatedDesc));
    } catch (error) {
      console.error('Error fetching authorized personnel:', error);
      toast.error(tRef.current('customers.personnel.toast.loadError'));
    } finally {
      setLoading(false);
    }
  }, [customer.id]);

  useEffect(() => {
    fetchPersonnel();
  }, [fetchPersonnel]);

  const setValue = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const openForm = (mode, person) => {
    setValues(person ? { ...person } : EMPTY_PERSON);
    setErrors({});
    setEditing({ mode });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const found = {};
    if (!String(values.firstName || '').trim()) found.firstName = t('dataTable.form.required');
    if (!String(values.lastName || '').trim()) found.lastName = t('dataTable.form.required');
    if (values.email && !EMAIL_PATTERN.test(String(values.email).trim())) found.email = t('customers.personnel.emailInvalid');
    setErrors(found);
    if (found.firstName || found.lastName) {
      toast.error(t('customers.personnel.required'));
      return;
    }
    if (found.email) return;

    const isEdit = editing.mode === 'edit';
    setSaving(true);
    try {
      if (isEdit) {
        await updateDoc(doc(db, 'CustomerAuthorizedPersonnel', values.id), { ...values, updatedAt: serverTimestamp() });
        toast.success(t('customers.personnel.toast.updated'));
      } else {
        await addDoc(collection(db, 'CustomerAuthorizedPersonnel'), {
          ...values,
          customerId: customer.id,
          customerName: customerName(customer),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        toast.success(t('customers.personnel.toast.added'));
      }
      setEditing(null);
      fetchPersonnel();
    } catch (error) {
      console.error(isEdit ? 'Error updating personnel:' : 'Error adding personnel:', error);
      toast.error(t(isEdit ? 'customers.personnel.toast.updateError' : 'customers.personnel.toast.addError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (person) => {
    try {
      await deleteDoc(doc(db, 'CustomerAuthorizedPersonnel', person.id));
      toast.success(t('customers.personnel.toast.deleted'));
      await fetchPersonnel();
    } catch (error) {
      console.error('Error deleting personnel:', error);
      toast.error(t('customers.personnel.toast.deleteError'));
    }
  };

  const field = (name, props = {}) => (
    <TextInput id={`person-${name}`} value={values[name] || ''} onChange={(event) => setValue(name, event.target.value)} error={errors[name]} {...props} />
  );

  const renderForm = () => (
    <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
      <Field label={t('customers.personnel.fields.firstName')} htmlFor="person-firstName" required error={errors.firstName}>
        {field('firstName', { autoComplete: 'off' })}
      </Field>
      <Field label={t('customers.personnel.fields.lastName')} htmlFor="person-lastName" required error={errors.lastName}>
        {field('lastName', { autoComplete: 'off' })}
      </Field>
      <Field label={t('customers.personnel.fields.position')} htmlFor="person-position">
        {field('position', { autoComplete: 'off' })}
      </Field>
      <Field label={t('customers.personnel.fields.department')} htmlFor="person-department">
        {field('department', { autoComplete: 'off' })}
      </Field>
      <Field label={t('customers.personnel.fields.email')} htmlFor="person-email" error={errors.email}>
        {field('email', { type: 'email', inputMode: 'email', autoCapitalize: 'none', placeholder: t('customers.personnel.fields.emailPlaceholder') })}
      </Field>
      <Field label={t('customers.personnel.fields.phone')} htmlFor="person-phone">
        {field('phone', { type: 'tel', placeholder: t('customers.personnel.fields.phonePlaceholder') })}
      </Field>
      <Field label={t('customers.personnel.fields.notes')} htmlFor="person-notes" className="sm:tw-col-span-2">
        <TextArea
          id="person-notes"
          rows={3}
          value={values.notes || ''}
          onChange={(event) => setValue('notes', event.target.value)}
          placeholder={t('customers.personnel.fields.notesPlaceholder')}
        />
      </Field>
    </div>
  );

  const renderItem = (person) => {
    const role = [person.position, person.department].filter(Boolean).join(' · ');
    return (
      <div className="tw-flex tw-min-w-0 tw-items-start tw-gap-3">
        <span
          aria-hidden="true"
          className="tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-indigo-400 tw-to-blue-600 tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]"
        >
          {getInitials(person.firstName, person.lastName)}
        </span>
        <div className="tw-min-w-0 tw-flex-1">
          <p className="tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">
            {`${person.firstName || ''} ${person.lastName || ''}`.trim()}
          </p>
          {role && (
            <p className="tw-mt-0.5 tw-flex tw-min-w-0 tw-items-center tw-gap-1.5 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
              <LuBriefcase aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
              <span className="tw-truncate">{role}</span>
            </p>
          )}
          {(person.email || person.phone) && (
            <p className="tw-mt-1.5 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-text-sm">
              {person.email && (
                <a href={`mailto:${person.email}`} className="tw-inline-flex tw-min-w-0 tw-max-w-full tw-items-center tw-gap-1.5 tw-text-slate-600 tw-transition-colors hover:tw-text-teal-700 dark:tw-text-slate-300 dark:hover:tw-text-teal-300">
                  <LuMail aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                  <span className="tw-truncate">{person.email}</span>
                </a>
              )}
              {person.phone && (
                <a href={`tel:${String(person.phone).replace(/\s+/g, '')}`} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-slate-600 tw-tabular-nums tw-transition-colors hover:tw-text-teal-700 dark:tw-text-slate-300 dark:hover:tw-text-teal-300">
                  <LuPhone aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                  {person.phone}
                </a>
              )}
            </p>
          )}
          {person.notes && (
            <p className="tw-mt-2 tw-whitespace-pre-line tw-break-words tw-rounded-lg tw-bg-slate-900/[0.03] tw-px-2.5 tw-py-1.5 tw-text-xs tw-text-slate-500 dark:tw-bg-white/[0.04] dark:tw-text-slate-400">
              {person.notes}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <RecordListModal
      title={t('customers.personnel.title')}
      subtitle={customerName(customer)}
      icon={LuUsers}
      gradient="tw-from-indigo-400 tw-to-blue-600 tw-shadow-indigo-500/30"
      countLabel={t('customers.personnel.count', { count: personnel.length })}
      addLabel={t('customers.personnel.add')}
      emptyLabel={t('customers.personnel.empty')}
      emptyHint={t('customers.personnel.emptyHint')}
      deleteConfirmLabel={t('customers.personnel.deleteConfirm')}
      items={personnel}
      loading={loading}
      renderItem={renderItem}
      editing={editing}
      formTitle={editing?.mode === 'edit' ? t('customers.personnel.editTitle') : t('customers.personnel.addTitle')}
      renderForm={renderForm}
      saving={saving}
      onAdd={() => openForm('add')}
      onEdit={(person) => openForm('edit', person)}
      onDelete={handleDelete}
      onCancelForm={() => setEditing(null)}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  );
};

AuthorizedPersonnelModal.propTypes = {
  customer: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AuthorizedPersonnelModal;
